import { ChangeDetectorRef, Component } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CsAgentAuthService } from '@spartacus/asm/root';
import { UserAccountFacade } from '@spartacus/user/account/root';
import {
  BehaviorSubject,
  combineLatest,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { OrderHistoryService } from '../../order-history/order-history.service';
import { CustomInfluenzaService } from '../custom-influenza.service';
import { OrderItem, QUOTE_OPEN_STATUSES } from 'src/app/shared/model';
import { DATE_FORMAT, UserUtils } from 'src/app/shared';
import { ProductDetailsComponent } from 'src/app/custom-add-to-cart/custom-add-to-cart/product-details/product-details.component';

@Component({
  selector: 'app-influenza-dashboard-reservation-table',
  templateUrl: './influenza-dashboard-reservation-table.component.html',
  styleUrls: ['./influenza-dashboard-reservation-table.component.scss'],
})
export class InfluenzaDashboardReservationTableComponent {
  ordersList = [];
  totalOrders: number = 0;
  orders = [];
  DATE_FORMAT = DATE_FORMAT;
  xlsDownloaded: boolean = false;
  pageLength = '8 per page';
  pageList = [this.pageLength, '16 per page', '24 per page'];
  pageSizeperPage = this.pageLength;
  pageSize = 8;
  curPage = 1;
  end: number;
  filters: {
    seasonId: string;
    pageSize: number;
    orderStatus: string;
    fields: string;
    currentPage: number;
  } = {
    fields: 'DEFAULT',
    currentPage: this.curPage - 1,
    pageSize: this.pageSize,
    seasonId: '',
    orderStatus: '',
  };
  userId;
  allOrdersSubscription: any;
  seasonSubscriber: Subscription;
  csAgentAuthSubscription: Subscription;
  userInfo: any;
  userDataSubscription: any;
  orderHistorySubscriber: any;
  isAsmEnabled$: Observable<boolean> = this.csAgentAuthService
    .isCustomerEmulated()
    .pipe(map((el) => !!el));
  orderHistory: any = ['1'];
  expandedRows: boolean[] = [];
  seasonFrameList: string[] = [];
  statusFrameList = [
    { key: 'ALL', value: 'All' },
    { key: 'CHECKED_VALID', value: 'Checked Valid' },
    { key: 'CREATED', value: 'Confirmed' },
    { key: 'WAITING_FOR_CONFIRMATION', value: 'Waiting' },
    { key: 'APPROVED', value: 'Approved' },
    { key: 'DECLINED', value: 'Declined' },
    { key: 'NOT_CONFIRMED', value: 'Not Confirmed' },
  ];
  searchTerm: any;
  selectedStatus: string | null = null; // Initialize to null or any default value
  selectedSeasonFrame: string | null = null; // Initialize to null or any default value

  openRequestsToggled$ = new BehaviorSubject<boolean>(true);
  reservationList$: Observable<OrderItem[]> = combineLatest([
    this.openRequestsToggled$,
    this.fluService.allReservations,
  ]).pipe(
    map(([openRequestsOnly, allReservations]) => {
      return openRequestsOnly
        ? allReservations.filter((reservation) =>
            QUOTE_OPEN_STATUSES.includes(reservation.state)
          )
        : allReservations;
    })
  );

  isPharmacyUser$ = this.user.get().pipe(map(UserUtils.isPharmacyUser));

  constructor(
    private readonly service: OrderHistoryService,
    private readonly fluService: CustomInfluenzaService,
    private readonly user: UserAccountFacade,
    private readonly cd: ChangeDetectorRef,
    private readonly modalService: NgbModal,
    private readonly csAgentAuthService: CsAgentAuthService
  ) {}

  ngOnInit() {
    this.end = this.pageSize;
    this.getAllReservations();
    this.cd.detectChanges();
  }

  generateXls(allOrders) {
    const list = JSON.parse(JSON.stringify(allOrders));
    const orders = [];
    list.forEach((element) => {
      orders.push({
        Season: element.seasonId,
        Account: element.soldToId,
        'Account Name': element.soldToName,
        Postcode: element.soldToPostalCode,
        Reservation: element.guid,
        'Unit/Doses': `${element.sumQuantity} / ${element.sumDoses}`,
        'Delivery Drops': element.deliveryDrops,
        'Order Status': element.statusDisplay,
      });
    });
    this.service.downloadXls(orders, 'OrderHistory');
    this.xlsDownloaded = true;
  }

  changePageSize() {
    this.curPage = 1;
    if (this.pageSizeperPage === this.pageLength) {
      this.pageSize = 8;
    }
    if (this.pageSizeperPage === '16 per page') {
      this.pageSize = 16;
    }
    if (this.pageSizeperPage === '24 per page') {
      this.pageSize = 24;
    }
    if (this.pageSizeperPage === 'All') {
      this.pageSize = this.totalOrders;
    }
    if (this.pageSize < this.totalOrders) {
      this.end = this.pageSize;
    } else {
      this.end = this.totalOrders;
    }
  }

  getCurrentPage(type) {
    this.curPage = type === 'prev' ? this.curPage - 1 : this.curPage + 1;
    this.cd.detectChanges();
  }

  getAllReservations() {
    this.userInfo = this.user.get().subscribe((userData) => {
      this.userId = userData;
      this.filters = this.getFilterQuery();
      if (userData) {
        this.fluService.getAllReservations();
      }
    });
  }

  numberOfPages() {
    if (this.ordersList && this.totalOrders && this.pageSize > 0) {
      return Math.ceil(this.totalOrders / this.pageSize);
    } else {
      return 1;
    }
  }

  toggleRow(index: number): void {
    this.expandedRows[index] = !this.expandedRows[index];
  }

  getFilterQuery() {
    return {
      fields: 'DEFAULT',
      currentPage: this.curPage - 1,
      pageSize: this.pageSize,
      searchTerm: this.searchTerm || '',
      orderStatus: this.selectedStatus || '',
      seasonId:
        this.selectedSeasonFrame === 'All'
          ? ''
          : this.selectedSeasonFrame || '',
    };
  }

  productDetails(productDetails) {
    const modalRef = this.modalService.open(ProductDetailsComponent, {
      windowClass: 'product-details-inf-modal',
    });
    modalRef.componentInstance.productDetails = productDetails;
  }

  onFilterChange() {
    this.getAllReservations();
  }
  onFilterStatusChange(event: any) {
    const selected = this.statusFrameList.find(
      (status) => status.value === event.value
    );
    this.selectedStatus = selected.key || this.statusFrameList[0].key;
    this.getAllReservations();
  }

  ngOnDestroy() {
    this.userDataSubscription?.unsubscribe();
    this.seasonSubscriber?.unsubscribe();
  }

  hasValidDeliveryDate(date: string | undefined | null): boolean {
    if (!date || date.trim() === '') {
      return false;
    }
    const deliveryDate = new Date(date);
    return deliveryDate.getDate() === 31 && deliveryDate.getMonth() === 11;
  }

  toggleOpenRequests(): void {
    this.openRequestsToggled$.next(!this.openRequestsToggled$.getValue());
  }
}
